import { Button, Container, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import AccountFilters from "../components/accounts/AccountFilters";
import AccountModal, {
	type AccountFormData,
} from "../components/accounts/AccountModal";
import AccountOverview from "../components/accounts/AccountOverview";
import AccountTable, {
	type AccountSortField,
	type SortDirection,
} from "../components/accounts/AccountTable";
import BillingHistoryModal from "../components/accounts/BillingHistoryModal";
import EmailReportModal from "../components/accounts/EmailReportModal";
import EmptyState from "../components/common/EmptyState";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Pagination from "../components/common/Pagination";
import {
	clearError,
	createAccount,
	deleteAccount,
	exportAccountLicenses,
	exportAccountStatusReport,
	fetchAccounts,
	fetchAccountStats,
	fetchFilteredAccountStats,
	clearFilteredStats,
	sendAccountReport,
	setPage,
	updateAccount,
	removeScheduledReport,
	type Account,
} from "../store/accounts/accountsSlice";
import { accountsAPI, type BillingReportSummary } from "../services/api";
import type { AppDispatch, RootState } from "../store/store";

function AccountsPage() {
	const dispatch = useDispatch<AppDispatch>();
	const { accounts, loading, error, total, page, limit, stats, filteredStats } =
		useSelector((state: RootState) => state.accounts);
	const { user: currentUser } = useSelector((state: RootState) => state.auth);

	const [modalOpened, setModalOpened] = useState(false);
	const [editingAccount, setEditingAccount] = useState<Account | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [emailModalOpened, setEmailModalOpened] = useState(false);
	const [emailSending, setEmailSending] = useState(false);
	const [currentExportAccount, setCurrentExportAccount] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [reportType, setReportType] = useState<"billing" | "status">("billing");
	const [sortField, setSortField] = useState<AccountSortField>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [historyAccount, setHistoryAccount] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [billingReports, setBillingReports] = useState<BillingReportSummary[]>([]);
	const [billingReportsLoading, setBillingReportsLoading] = useState(false);

	// AccountFilters also reports the search when it mounts; resetting the page
	// then would throw away the page kept in the store across visits.
	const handleSearchChange = useCallback(
		(value: string) => {
			if (value === searchTerm) return;
			setSearchTerm(value);
			dispatch(setPage(1));
		},
		[dispatch, searchTerm]
	);

	// Sorting is applied by the API over the whole result set. Reordering the
	// rows here would only ever reach the ten already on screen.
	const handleSort = (field: AccountSortField) => {
		if (sortField === field) {
			// Toggle direction or clear sort
			if (sortDirection === "desc") {
				setSortDirection("asc");
			} else if (sortDirection === "asc") {
				setSortField(null);
				setSortDirection(null);
			} else {
				setSortDirection("desc");
			}
		} else {
			setSortField(field);
			setSortDirection("desc");
		}
		dispatch(setPage(1));
	};

	const totalPages = Math.ceil(total / limit);

	// Refetching through the effect below rather than dispatching here keeps one
	// source of the page number: a delete can shrink the list under the current
	// page while the clamp below is moving it back.
	const refreshData = () => {
		setRefreshKey((key) => key + 1);
		dispatch(fetchAccountStats());
	};

	useEffect(() => {
		dispatch(fetchAccountStats());
	}, [dispatch]);

	// Filtered counts sit under each Overview total, over exactly what the
	// table shows. refreshKey: an edit can move accounts in or out of the search.
	useEffect(() => {
		if (!searchTerm) {
			dispatch(clearFilteredStats());
			return;
		}
		dispatch(fetchFilteredAccountStats({ search: searchTerm }));
	}, [dispatch, searchTerm, refreshKey]);

	useEffect(() => {
		dispatch(
			fetchAccounts({
				page,
				limit,
				search: searchTerm,
				sortBy: sortField ?? undefined,
				sortOrder: sortDirection ?? undefined,
			})
		);
	}, [dispatch, page, limit, searchTerm, sortField, sortDirection, refreshKey]);

	// Deleting the only row on the last page left the page past the end, which
	// rendered as "no accounts found" with no pagination to get back with.
	useEffect(() => {
		if (totalPages > 0 && page > totalPages) {
			dispatch(setPage(totalPages));
		}
	}, [dispatch, page, totalPages]);

	useEffect(() => {
		if (error) {
			notifications.show({
				title: "Error",
				message: error,
				color: "red",
			});
			dispatch(clearError());
		}
	}, [error, dispatch]);

	// The account saves even when its report subscribers could not be
	// updated; the backend says so in syncErrors, which used to be dropped
	// behind a green "success" - so a removed contact kept getting reports.
	const showSaveResult = (result: { data?: unknown }, successMessage: string) => {
		const syncErrors = (result.data as { syncErrors?: string[] } | undefined)?.syncErrors;
		notifications.show(
			syncErrors?.length
				? {
						title: "Saved, but report recipients were not updated",
						message: syncErrors.join("; "),
						color: "yellow",
					}
				: { title: "Success", message: successMessage, color: "green" },
		);
	};

	// Rejects when the save fails, so AccountModal keeps what was typed; the
	// error itself is shown from Redux.
	const handleCreateAccount = async (values: AccountFormData) => {
		const createData = {
			...values,
			tags: values.tags || [],
		};

		const result = await dispatch(createAccount(createData)).unwrap();
		setModalOpened(false);
		refreshData();
		showSaveResult(result, "Account created successfully");
	};

	const handleUpdateAccount = async (values: AccountFormData) => {
		if (!editingAccount) return;

		const result = await dispatch(
			updateAccount({
				id: editingAccount._id,
				...values,
				tags: values.tags || [],
				isActive: editingAccount.isActive, // Preserve existing isActive status
			})
		).unwrap();
		setModalOpened(false);
		setEditingAccount(null);
		refreshData();
		showSaveResult(result, "Account updated successfully");
	};

	const handleDeleteAccount = (id: string, name: string) => {
		modals.openConfirmModal({
			title: "Delete Account",
			children: (
				<Text>
					Are you sure you want to delete the account "{name}"? This cannot be
					undone. An account that still has licenses cannot be deleted; delete
					its licenses first.
				</Text>
			),
			labels: { confirm: "Delete", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					await dispatch(deleteAccount(id)).unwrap();
					refreshData();
					notifications.show({
						title: "Success",
						message: "Account deleted successfully",
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const handleExportAccount = async (
		accountId: string,
		accountName: string
	) => {
		try {
			await dispatch(
				exportAccountLicenses({ accountId, accountName })
			).unwrap();
			notifications.show({
				title: "Success",
				message: "Excel report downloaded successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux and shown via useEffect
		}
	};

	const handleEmailExport = (accountId: string, accountName: string) => {
		setCurrentExportAccount({ id: accountId, name: accountName });
		setReportType("billing");
		setEmailModalOpened(true);
	};

	const showRequestError = (error: unknown, fallback: string) => {
		const message = (error as { response?: { data?: { message?: string } } })
			?.response?.data?.message;
		notifications.show({ title: "Error", message: message || fallback, color: "red" });
	};

	const handleShowBillingHistory = async (
		accountId: string,
		accountName: string
	) => {
		setHistoryAccount({ id: accountId, name: accountName });
		setBillingReports([]);
		setBillingReportsLoading(true);
		try {
			const response = await accountsAPI.getBillingReports(accountId);
			setBillingReports(response.data.data?.reports ?? []);
		} catch (error) {
			showRequestError(error, "Failed to load past billing reports");
		} finally {
			setBillingReportsLoading(false);
		}
	};

	const handleDownloadBillingReport = async (report: BillingReportSummary) => {
		if (!historyAccount) return;
		try {
			const response = await accountsAPI.downloadBillingReport(
				historyAccount.id,
				report.period
			);
			const url = window.URL.createObjectURL(response.data);
			const link = document.createElement("a");
			link.href = url;
			link.download = report.filename;
			link.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			showRequestError(error, "Failed to download billing report");
		}
	};

	const handleExportStatusReport = async (
		accountId: string,
		accountName: string
	) => {
		try {
			await dispatch(
				exportAccountStatusReport({ accountId, accountName })
			).unwrap();
			notifications.show({
				title: "Success",
				message: "Status report downloaded successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux and shown via useEffect
		}
	};

	const handleEmailStatusReport = (accountId: string, accountName: string) => {
		setCurrentExportAccount({ id: accountId, name: accountName });
		setReportType("status");
		setEmailModalOpened(true);
	};

	// A broadcast is routinely part-successful, so a green notification alone would misreport it
	const notifySendResult = (
		sent: string[],
		failed: { email: string; reason: string }[],
		scheduledOnly: boolean
	) => {
		if (sent.length > 0) {
			notifications.show({
				title: "Success",
				message: `Report sent to ${sent.join(", ")}`,
				color: "green",
			});
		}

		if (failed.length > 0) {
			notifications.show({
				title: "Some recipients failed",
				message: failed.map((f) => `${f.email}: ${f.reason}`).join("; "),
				color: "red",
				autoClose: false,
			});
		}

		if (scheduledOnly) {
			notifications.show({
				title: "Success",
				message: "Monthly auto-send has been enabled",
				color: "green",
			});
		}
	};

	const handleSendEmail = async (
		emails: string[],
		scheduleMonthly: boolean,
		sendNow: boolean
	) => {
		if (!currentExportAccount) return;

		setEmailSending(true);
		try {
			const result = await dispatch(
				sendAccountReport({
					accountId: currentExportAccount.id,
					reportType,
					// A schedule-only request still needs the addresses subscribed, not mailed
					emails,
					toSubscribers: false,
					scheduleMonthly,
					sendNow,
				})
			).unwrap();

			notifySendResult(result.sent, result.failed, scheduleMonthly && !sendNow);

			setEmailModalOpened(false);
			setCurrentExportAccount(null);
		} catch {
			// Error handled by Redux and shown via useEffect
		} finally {
			setEmailSending(false);
		}
	};

	const handleSendToSubscribers = (
		accountId: string,
		accountName: string,
		type: "billing" | "status"
	) => {
		const account = accounts.find((acc) => acc._id === accountId);
		const recipients = account?.scheduledReports?.map((r) => r.email) ?? [];

		modals.openConfirmModal({
			title: `Send ${type === "billing" ? "Billing" : "Status"} Report`,
			children: (
				<Text size="sm">
					Send the {type} report for "{accountName}" to{" "}
					{recipients.length} subscriber(s)? {recipients.join(", ")}
				</Text>
			),
			labels: { confirm: "Send", cancel: "Cancel" },
			onConfirm: async () => {
				try {
					const result = await dispatch(
						sendAccountReport({
							accountId,
							reportType: type,
							toSubscribers: true,
						})
					).unwrap();

					notifySendResult(result.sent, result.failed, false);
				} catch {
					// Error handled by Redux and shown via useEffect
				}
			},
		});
	};

	const handleRemoveScheduledReport = async (
		accountId: string,
		email: string,
		accountName: string
	) => {
		modals.openConfirmModal({
			title: "Remove Scheduled Report",
			children: (
				<Text>
					Are you sure you want to remove the monthly scheduled report to{" "}
					{email} for account "{accountName}"?
				</Text>
			),
			labels: { confirm: "Remove", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					await dispatch(removeScheduledReport({ accountId, email })).unwrap();
					notifications.show({
						title: "Success",
						message: `Scheduled report to ${email} removed successfully`,
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const openModal = (account?: Account) => {
		setEditingAccount(account || null);
		setModalOpened(true);
	};

	// Matches POST /api/accounts, which allows managers
	const canCreateAccounts = ["admin", "manager"].includes(
		currentUser?.role || ""
	);

	const pageActions = canCreateAccounts && (
		<Button
			leftSection={<IconPlus size={16} />}
			onClick={() => openModal()}>
			Create Account
		</Button>
	);

	return (
		<Container size="xl">
			<PageHeader
				title="Accounts"
				description="Manage customer accounts and licenses"
				actions={pageActions}
			/>

			<AccountOverview
				total={stats.total}
				active={stats.active}
				filtered={searchTerm ? filteredStats : null}
				filterLabel={searchTerm ? `"${searchTerm}"` : null}
			/>

			<AccountFilters
				searchTerm={searchTerm}
				onSearchChange={handleSearchChange}
			/>

			{loading ? (
				<LoadingSpinner />
			) : accounts.length === 0 ? (
				<EmptyState
					title="No accounts found"
					message={
						searchTerm
							? "No accounts match your search criteria."
							: "No accounts have been created yet. Create your first account to get started."
					}
				/>
			) : (
				<AccountTable
					accounts={accounts}
					currentUserRole={currentUser?.role}
					sortField={sortField}
					sortDirection={sortDirection}
					onSort={handleSort}
					onEditAccount={openModal}
					onDeleteAccount={handleDeleteAccount}
					onExportAccount={handleExportAccount}
					onEmailExport={handleEmailExport}
					onShowBillingHistory={handleShowBillingHistory}
					onExportStatusReport={handleExportStatusReport}
					onEmailStatusReport={handleEmailStatusReport}
					onSendToSubscribers={handleSendToSubscribers}
					onRemoveScheduledReport={handleRemoveScheduledReport}
				/>
			)}

			{/* Outside the empty branch: a page past the end renders empty, and
			    the controls are the way back from it. */}
			<Pagination
				currentPage={page}
				totalPages={totalPages}
				onPageChange={(newPage) => dispatch(setPage(newPage))}
			/>

			<AccountModal
				opened={modalOpened}
				account={editingAccount}
				loading={loading}
				onClose={() => setModalOpened(false)}
				onSubmit={editingAccount ? handleUpdateAccount : handleCreateAccount}
			/>

			<EmailReportModal
				opened={emailModalOpened}
				accountName={currentExportAccount?.name || ""}
				reportType={reportType}
				loading={emailSending}
				onClose={() => {
					setEmailModalOpened(false);
					setCurrentExportAccount(null);
				}}
				onSubmit={handleSendEmail}
			/>

			<BillingHistoryModal
				opened={historyAccount !== null}
				accountName={historyAccount?.name || ""}
				reports={billingReports}
				loading={billingReportsLoading}
				onClose={() => setHistoryAccount(null)}
				onDownload={handleDownloadBillingReport}
			/>
		</Container>
	);
}

export default AccountsPage;
